import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DeletionStatus {
  id: string;
  user_id: string;
  deletion_requested_at: string;
  deletion_scheduled_at: string;
  reason?: string;
  is_cancelled: boolean;
  days_remaining: number;
}

const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({ isOpen, onClose }) => {
  // const { logout } = useAuth(); // Removed unused variable
  const [step, setStep] = useState<'warning' | 'reason' | 'confirm' | 'success' | 'status'>('warning');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);

  // Check for existing deletion status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkDeletionStatus();
    }
  }, [isOpen]);

  // If deletion is already scheduled, show status immediately
  useEffect(() => {
    if (deletionStatus && step === 'warning') {
      setStep('status');
    }
  }, [deletionStatus, step]);

  const checkDeletionStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setDeletionStatus(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/account-deletion-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.id) {
          setDeletionStatus(data);
          setStep('status');
        }
      } else if (response.status === 404) {
        // API endpoint doesn't exist yet (backend not running)
        setDeletionStatus(null);
      }
    } catch (error) {
      // Network error or backend not running - silently fail
      setDeletionStatus(null);
    }
  };

  const handleDeleteRequest = async () => {
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/request-account-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to request account deletion');
      }

      const data = await response.json();
      setDeletionStatus(data);
      setStep('success');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to request account deletion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/cancel-account-deletion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel account deletion');
      }

      setDeletionStatus(null);
      setStep('warning');
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to cancel account deletion');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('warning');
    setReason('');
    setError(null);
    setLoading(false);
    setDeletionStatus(null);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        background: 'rgba(20, 20, 20, 0.95)',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        border: '1px solid rgba(255, 71, 87, 0.3)',
        boxShadow: '0 25px 50px rgba(255, 71, 87, 0.3)',
        position: 'relative',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '35px',
            height: '35px',
            color: 'rgba(255, 255, 255, 0.7)',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
          }}
        >
          ×
        </button>

        {/* Warning Step */}
        {step === 'warning' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{
              color: '#ff4757',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 20px 0',
            }}>
              Delete Your Account?
            </h2>
            <div style={{
              background: 'rgba(255, 71, 87, 0.1)',
              border: '1px solid rgba(255, 71, 87, 0.3)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '25px',
              textAlign: 'left',
            }}>
              <h3 style={{
                color: '#ff4757',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 15px 0',
              }}>
                ⚠️ This action will permanently delete:
              </h3>
              <ul style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px',
                lineHeight: '1.6',
                margin: 0,
                paddingLeft: '20px',
              }}>
                <li>All your conversations with AI companions</li>
                <li>Your profile information and preferences</li>
                <li>Your account history and statistics</li>
                <li>All associated data and settings</li>
              </ul>
            </div>
            <div style={{
              background: 'rgba(0, 255, 136, 0.1)',
              border: '1px solid rgba(0, 255, 136, 0.3)',
              borderRadius: '15px',
              padding: '20px',
              marginBottom: '30px',
              textAlign: 'left',
            }}>
              <h3 style={{
                color: '#00ff88',
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 10px 0',
              }}>
                💡 Grace Period: 5 Days
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.5',
              }}>
                You'll have 5 days to change your mind and cancel the deletion. 
                After that, your account will be permanently deleted.
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
            }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '15px 25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                }}
              >
                Keep My Account
              </button>
              <button
                onClick={() => setStep('reason')}
                style={{
                  padding: '15px 25px',
                  background: 'linear-gradient(135deg, #ff4757 0%, #c44569 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                }}
              >
                🗑️ Continue Deletion
              </button>
            </div>
          </div>
        )}

        {/* Reason Step */}
        {step === 'reason' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>💭</div>
              <h2 style={{
                color: 'white',
                fontSize: '24px',
                fontWeight: '700',
                margin: '0 0 10px 0',
              }}>
                Help Us Improve
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '16px',
                margin: 0,
              }}>
                Would you mind sharing why you're leaving? (Optional)
              </p>
            </div>

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us what we could do better..."
              style={{
                width: '100%',
                height: '120px',
                padding: '15px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '15px',
                fontSize: '14px',
                color: 'white',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                marginBottom: '25px',
              }}
            />

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
            }}>
              <button
                onClick={() => setStep('warning')}
                style={{
                  padding: '15px 25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep('confirm')}
                style={{
                  padding: '15px 25px',
                  background: 'linear-gradient(135deg, #ff4757 0%, #c44569 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Confirm Step */}
        {step === 'confirm' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔥</div>
            <h2 style={{
              color: '#ff4757',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 20px 0',
            }}>
              Final Confirmation
            </h2>
            <div style={{
              background: 'rgba(255, 71, 87, 0.2)',
              border: '1px solid rgba(255, 71, 87, 0.4)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '25px',
            }}>
              <p style={{
                color: 'white',
                fontSize: '16px',
                margin: '0 0 15px 0',
                fontWeight: '600',
              }}>
                Are you absolutely sure you want to delete your account?
              </p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.5',
              }}>
                Your account will be scheduled for deletion in 5 days. You can cancel 
                this action anytime before then by logging back in.
              </p>
            </div>

            {error && (
              <div style={{
                padding: '15px',
                background: 'rgba(255, 71, 87, 0.2)',
                border: '1px solid rgba(255, 71, 87, 0.3)',
                borderRadius: '10px',
                color: '#ff4757',
                fontSize: '14px',
                marginBottom: '20px',
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
            }}>
              <button
                onClick={() => setStep('reason')}
                disabled={loading}
                style={{
                  padding: '15px 25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '25px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={loading}
                style={{
                  padding: '15px 25px',
                  background: loading 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'linear-gradient(135deg, #ff4757 0%, #c44569 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: loading ? 0.7 : 1,
                  minWidth: '180px',
                }}
              >
                {loading ? '🔥 Scheduling...' : '🗑️ Delete My Account'}
              </button>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && deletionStatus && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⏰</div>
            <h2 style={{
              color: '#ffa502',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 20px 0',
            }}>
              Account Deletion Scheduled
            </h2>
            <div style={{
              background: 'rgba(255, 165, 2, 0.1)',
              border: '1px solid rgba(255, 165, 2, 0.3)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '25px',
            }}>
              <p style={{
                color: 'white',
                fontSize: '16px',
                margin: '0 0 15px 0',
                fontWeight: '600',
              }}>
                Your account will be permanently deleted on:
              </p>
              <div style={{
                color: '#ffa502',
                fontSize: '18px',
                fontWeight: '700',
                marginBottom: '15px',
              }}>
                📅 {formatDate(deletionStatus.deletion_scheduled_at)}
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.5',
              }}>
                You have <strong>{deletionStatus.days_remaining} days</strong> to change your mind. 
                You can cancel this deletion by logging back into your account anytime before then.
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
            }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '15px 25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                💔 Sign Out Now
              </button>
            </div>
          </div>
        )}

        {/* Status Step (for existing deletion requests) */}
        {step === 'status' && deletionStatus && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⏰</div>
            <h2 style={{
              color: '#ffa502',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 20px 0',
            }}>
              Account Deletion Pending
            </h2>
            <div style={{
              background: 'rgba(255, 165, 2, 0.1)',
              border: '1px solid rgba(255, 165, 2, 0.3)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '25px',
            }}>
              <p style={{
                color: 'white',
                fontSize: '16px',
                margin: '0 0 15px 0',
                fontWeight: '600',
              }}>
                Your account is scheduled for deletion on:
              </p>
              <div style={{
                color: '#ffa502',
                fontSize: '18px',
                fontWeight: '700',
                marginBottom: '15px',
              }}>
                📅 {formatDate(deletionStatus.deletion_scheduled_at)}
              </div>
              <div style={{
                background: 'rgba(255, 71, 87, 0.2)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '15px',
              }}>
                <div style={{
                  color: '#ff4757',
                  fontSize: '24px',
                  fontWeight: '700',
                }}>
                  {deletionStatus.days_remaining} days remaining
                </div>
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.5',
              }}>
                You can still cancel this deletion and keep your account.
              </p>
            </div>

            {error && (
              <div style={{
                padding: '15px',
                background: 'rgba(255, 71, 87, 0.2)',
                border: '1px solid rgba(255, 71, 87, 0.3)',
                borderRadius: '10px',
                color: '#ff4757',
                fontSize: '14px',
                marginBottom: '20px',
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
            }}>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  padding: '15px 25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '25px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                Close
              </button>
              <button
                onClick={handleCancelDeletion}
                disabled={loading}
                style={{
                  padding: '15px 25px',
                  background: loading 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: loading ? 0.7 : 1,
                  minWidth: '180px',
                }}
              >
                {loading ? 'Cancelling...' : 'Keep My Account'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        textarea::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default AccountDeletionModal;